import { NotificationService } from 'src/app/signalr/notification.service';
import { HttpClient } from '@angular/common/http';
import { PhdsoftErrorService, CollectionInfoModel, Util } from '@phdsoft/common'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/internal/Observable'
import { CollectionState, SelectElement } from './_states/collection.states'
import { CollectionApi } from './_api/collection.api'
import { environment } from 'src/environments/environment'
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { ClusterElementViewModel } from 'projects/phdsoft/common/src/lib/models/cluster-element.model';
import { ClusterState } from '../cluster/cluster.state';
import { InspectionPlanService } from '../inspection-plan/inspection-plan.service';
import { GaugingElement } from '@app/platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import {
  InspectionBulkDeleteRequest,
  InspectionBulkDeleteResponse
} from './collection-inspection/_models/inspection-bulk-delete.model';

@Injectable({ providedIn: 'root' })
export class CollectionService {

  private isInspectionsLoading = new BehaviorSubject<boolean>(false);
  castInspectionLoadStatus = this.isInspectionsLoading.asObservable();

  private isReportsWindowAvailable = new BehaviorSubject<boolean>(false);
  castReportsWindowStatus = this.isReportsWindowAvailable.asObservable();

  private isInspectionsLoaded = new BehaviorSubject<boolean>(false);
  castInspectionsLoaded = this.isInspectionsLoaded.asObservable();

  // Simple concurrency control
  private pendingManagePlanKeys = new Set<string>();

  constructor(
    private state: CollectionState,
    private api: CollectionApi,
    private errorService: PhdsoftErrorService,
    private http: HttpClient,
    private inspectionPlanService: InspectionPlanService,
    private clusterState: ClusterState,
    private noticationService: NotificationService,
  ) { }

  setInspectionLoadStatus(bool: boolean) {
    this.isInspectionsLoading.next(bool)
  }

  setReportsWindowStatus(bool: boolean) {
    this.isReportsWindowAvailable.next(bool)
  }

  setIsInspectionsLoadedStatus(bool: boolean) {
    this.isInspectionsLoaded.next(bool)
  }

  get currentCollection$(): Observable<CollectionInfoModel> {
    return this.state.currentCollection$
  }

  get selectInspectionElement$(): Observable<SelectElement> {
    return this.state.selectElement$
  }

  resetCollections(): void {
    this.state.resetCollection()
  }

  changeCurrentCollection(pCollection: CollectionInfoModel): void {
    this.state.changeCurrentCollection(pCollection)
  }

  updateCollectionId(pCollection: CollectionInfoModel, pCollectionId: string): void {
    this.state.updatedCollectionIdString(pCollection, pCollectionId)
  }

  getById(pCollectionId: string): CollectionInfoModel {
    return this.state.collection.find((x) => x.TempCollectionId === pCollectionId)
  }

  changeSelectElement(pAny: GaugingElement): void {
    this.state.changeSelectElement(null, null, pAny)
  }

  addElement(pAny: any, pDate: Date, select?: boolean): void {
    this.state.addElement(pAny, pDate, select)
  }

  removeElement(pAny: ElementResultInfo, collectionId: string): void {
    this.state.removeElement(pAny, collectionId)
  }

  addCollection(pCollection: CollectionInfoModel, showMessage: boolean = true): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        this.state.isUpdating = true
        this.state.addCollection(pCollection)
        this.state.changeCurrentCollection(pCollection)
        this.state.isUpdating = false
        if (pCollection.Type === '4c41a67f39114591ab80eef2fc88d5aa') {
          // this.errorService.snack('Success', 'Added new local FEA Collection')
          //this.noticationService.addSuccessNotification('Added new Local FEA Collection')
        } else if (pCollection.IsTemplate) {
          // this.errorService.snack('Success', 'Added new local template')
          //this.noticationService.addSuccessNotification('Added new Local Template')

        } else {
          // this.errorService.snack('Success', 'Added new local collection')
          //this.noticationService.addSuccessNotification('Added new Local collection')

        }
        resolve(pCollection)
      } catch (error) {
        this.state.isUpdating = false
        reject(error)
      }
    })
  }

  saveInspectionElement(pCollection: ElementResultInfo[], followupDate: Date, inspectionTypeName: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.state.isUpdating = true
      this.api.saveInspectionElement(pCollection, followupDate).subscribe(
        async (ret: any) => {
          // console.log(ret)
          //_pCollection.ElementInfo.normal.push(_target.section.sectionNormal.normalize());

          this.errorService.snack('Success', 'Successfully Updated');
          const data: Array<ClusterElementViewModel> = new Array<ClusterElementViewModel>();
          let isExecuted: boolean = false;
          
          for (const iterator of pCollection) {
            let datesFromChecklist = iterator.CheckList.map(x => x.Date)
            let uniqueDatesFromChecklist: any[] = [...new Set(datesFromChecklist)]

            isExecuted = !Util.isNullOrUndefined(iterator.CheckList.find(x => x.Value != ''));

            let datesFromChecklistNotClusterized: any[] = []
            if (this.clusterState.ClusterState.items.length == 0) {
              datesFromChecklistNotClusterized = datesFromChecklistNotClusterized.concat(uniqueDatesFromChecklist)
            } else {
              uniqueDatesFromChecklist.forEach(dateFromChecklist => {
                let test = this.clusterState.ClusterState.items.find(item =>
                  item.date === dateFromChecklist && item.typeId === iterator.InspectionTypeId
                )
                if (test == undefined) {
                  datesFromChecklistNotClusterized.push(dateFromChecklist)
                }
              })
            }
            datesFromChecklistNotClusterized = [...new Set(datesFromChecklistNotClusterized)]
            
            if(datesFromChecklistNotClusterized.length > 0){
              data.push({ 
                ClusterType: "Inspection",
                AssetId: iterator.AssetId,
                RegionIds: iterator.RegionIds,
                Dates: datesFromChecklistNotClusterized,
                InspectionTypeId: iterator.InspectionTypeId
              });
            }
          }

          // Handle managePlan with simple deduplication
          const managePlanKey = `${data[0]?.AssetId || pCollection[0].AssetId}_${pCollection[0].InspectionTypeId}_${followupDate.getTime()}`;
          
          if (!this.pendingManagePlanKeys.has(managePlanKey)) {
            this.pendingManagePlanKeys.add(managePlanKey);
            
            try {
              if (data.length > 0 && !isExecuted) {
                await this.inspectionPlanService.managePlan(data[0].AssetId, pCollection[0].InsertedRegionId, data[0].InspectionTypeId, followupDate, false, inspectionTypeName).toPromise();
              }
              else if (isExecuted) {
                await this.inspectionPlanService.managePlan(pCollection[0].AssetId, pCollection[0].InsertedRegionId, pCollection[0].InspectionTypeId, followupDate, true, inspectionTypeName).toPromise();
              }
            } catch (error) {
              // Thrown inside this callback, the error would leave the promise pending for good.
              reject(error);
              return;
            } finally {
              this.pendingManagePlanKeys.delete(managePlanKey);
            }
          }

          // Update cluster state
          for (const iterator of pCollection) {
            let datesFromChecklist = iterator.CheckList.map(x => x.Date)
            let uniqueDatesFromChecklist: any[] = [...new Set(datesFromChecklist)]
            uniqueDatesFromChecklist.forEach(x => {
              this.clusterState.checkClusterState({ date: x, typeId: iterator.InspectionTypeId })
            })
          }

          resolve(ret)
        },
        (error: any) => {
          this.state.isUpdating = false
          this.errorService.snack('Error', `Error saving`)
          reject(error)
        },
        () => {
          this.state.isUpdating = false
        },
      )
    })
  }

  getInspectionById(elementId: string) {
    const timestamp = Date.now();
    return this.http.get<ElementResultInfo>(environment.API_INSPEC + `Inspection/GetInspectionElementById/${elementId}?t=${timestamp}`)
  }

  deleteCollectionElement(
    pCollectionId: string,
    pAssetId: string,
    pElementId: string,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.state.isUpdating = true
      this.api.deleteCollectionElement(pCollectionId, pAssetId, pElementId).subscribe(
        (ret: any) => {
          if (ret) {
            this.state.isUpdating = false
            this.noticationService.addSuccessNotification('Successfully Delete Collection Element')
            // this.errorService.snack('Success', 'Successfully Delete Collection Element')
          } else {
            this.noticationService.addFailedNotification('Error when deleting collection Element')
            // this.errorService.snack('Error', `Error when deleting collection Element`)
          }
          resolve(ret)
        },
        (error: any) => {
          this.state.isUpdating = false
          this.noticationService.addFailedNotification('Error when deleting collection Element')
          // this.errorService.snack('Error', `Error when deleting collection Element`)
          reject(error)
        },
        () => (this.state.isUpdating = false),
      )
    })
  }

  deleteElementsFollowup(elements: ElementResultInfo[], startDate: Date, endDate: Date): Promise<ElementResultInfo[]> {
    return new Promise((resolve, reject) => {
      this.state.isUpdating = true
      this.api.deleteElementsFollowup(elements, startDate, endDate).subscribe(
        (ret: any) => {
          if (ret) {
            this.state.isUpdating = false
            // this.noticationService.addSuccessNotification('Successfully Delete Collection Element')
          } else {
            // this.noticationService.addFailedNotification('Error when deleting collection Element')
          }
          resolve(ret)
        },
        (error: any) => {
          this.state.isUpdating = false
          // this.noticationService.addFailedNotification('Error when deleting collection Element')
          reject(error)
        },
        () => (this.state.isUpdating = false),
      )
    })
  }

  async bulkDelete(request: InspectionBulkDeleteRequest): Promise<InspectionBulkDeleteResponse> {
    this.state.isUpdating = true;
    try {
      return await firstValueFrom(this.api.bulkDelete(request));
    } finally {
      this.state.isUpdating = false;
    }
  }

  setElementsLockedStatus(filterId: string, lockedStatus: boolean) {
    return this.http.post<any>(
      environment.API_INSPEC + `Inspection/SetElementsLockedStatus?filterId=${filterId}&lockedStatus=${lockedStatus}`,
      null
    )
  }

  async getNextLabelSequence(assetId: string, regionId: string, sectionId: string, inspectionTypeId: string): Promise<string> {
    try {
      const timestamp = Date.now();
      let nextLabelSequenceObservable = this.http.get<number>(environment.API_INSPEC + `Inspection/GetNextLabelSequence?assetId=${assetId}&regionId=${regionId}&sectionId=${sectionId}&inspectionTypeId=${inspectionTypeId}&t=${timestamp}`)
      let nextLabelSequence = await firstValueFrom(nextLabelSequenceObservable);
      return nextLabelSequence.toString();
    } catch (error) {
      console.error(error);
      return "";
    }
  }

  updateInspectionLabels(assetId: string) {
    return this.http.post(environment.API_INSPEC + `Inspection/UpdateInspectionLabels/${assetId}`, null)
  }
}
